'use client';

import { Globe } from 'lucide-react';
import { usePathname as useNextPathname, useRouter as useNextRouter } from 'next/navigation';
import { useLocale } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LOCALE_CONFIG } from '@/lib/constants';
import { navigateToLocale } from '@/lib/utils/locale-navigation';

interface LanguageSwitcherProps {
  supportedLocales?: readonly string[];
}

export function LanguageSwitcher({ supportedLocales }: LanguageSwitcherProps) {
  const currentLocale = useLocale();
  const nextRouter = useNextRouter();
  const nextPathname = useNextPathname();

  const filteredLocales = supportedLocales
    ? LOCALE_CONFIG.filter((config) => supportedLocales.includes(config.code))
    : LOCALE_CONFIG;

  const handleLocaleChange = (newLocale: string) => {
    if (nextPathname?.startsWith('/app')) {
      // For app routes, use cookie-based locale switching
      document.cookie = `bite-club-locale=${newLocale}; path=/; max-age=31536000`; // 1 year
      nextRouter.refresh();
    } else {
      // For public routes, use the shared navigation utility
      navigateToLocale(nextPathname, newLocale);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="cursor-pointer">
          <Globe className="h-5 w-5" />
          <span className="sr-only">Switch language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {filteredLocales.map((config) => (
          <DropdownMenuItem
            key={config.code}
            onClick={() => handleLocaleChange(config.code)}
            className="cursor-pointer"
          >
            <span className="mr-2">{config.flag}</span>
            <span className={currentLocale === config.code ? 'font-bold' : ''}>{config.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
